import React, { FC, ReactNode } from 'react';
import { NavHashLink } from 'react-router-hash-link';

import { Box, Stack, Typography, TypographyProps, useDesktop, useMediaSwitch, useMobile } from 'Modules/ui3';
import { useTranslation } from 'Modules/core/i18n';

import { ReactComponent as CFADesktop } from './CFA_Desktop.svg';
import { ReactComponent as CFATablet } from './CFA_Tablet.svg';
import { ReactComponent as CFAMobile } from './CFA_Mobile.svg';
import { ReactComponent as MTSLogo } from './MTSLogo.svg';
import { OPERATOR_EMAIL, PR_EMAIL } from '../../constants';


interface EmailProps {
    title: ReactNode;
    email: string;
}

const Title: FC<TypographyProps> = props => {
    const isDesktop = useDesktop();
    return (
        <Typography
            fontFamily='Hauora'
            fontSize={useMediaSwitch('20px', '18px', '16px')}
            fontWeight={500}
            lineHeight={isDesktop ? '32px' : '24px'}
            color='#969592'
            {...props}
        />
    );
};

const Email: FC<EmailProps> = ({ email, title, children }) => {
    const isMobile = useMobile();
    return (
        <Stack spacing={isMobile ? 3 : 4} flex='1'>
            <Title>
                {title}
            </Title>
            <Typography
                component='a'
                href={`mailto:${email}`}
                fontFamily='Hauora'
                fontSize={isMobile ? '20px' : '32px'}
                fontWeight={700}
                lineHeight={isMobile ? '32px' : '40px'}
                color='#242424'
            >
                {email}
            </Typography>
            {children}
        </Stack>
    );
};

export const Footer: FC = () => {
    const isDesktop = useDesktop();
    const isMobile = useMobile();
    const Logo = useMediaSwitch(CFADesktop, CFATablet, CFAMobile);
    const { t } = useTranslation('footer');
    return (
        <Stack
            alignItems='center'
            px={useMediaSwitch(12, 6, 5)}
            pt={useMediaSwitch(37, 26, 22)}
            pb={useMediaSwitch(12, 6, 5)}
            mt={useMediaSwitch(0, -10, -8)}
            bgcolor={isDesktop ? '#fff' : '#F4F4F4'}
        >
            <Stack
                spacing={useMediaSwitch(37, 22, 12)}
                maxWidth='1200px'
                width='100%'
            >
                <Stack spacing={isDesktop ? 12 : 8} alignItems='flex-start'>
                    <Logo
                        style={{
                            width: isMobile ? '100%' : 'auto',
                            height: useMediaSwitch('148px', '120px', 'auto'),
                        }}
                    />
                    <Typography
                        fontFamily='Hauora'
                        fontSize={isMobile ? '16px' : '24px'}
                        fontWeight={600}
                        lineHeight={isMobile ? '24px' : '32px'}
                        color='#242424'
                    >
                        {t('about')}
                        <Box
                            component='span'
                            display='inline-block'
                            ml={2}
                            sx={{
                                verticalAlign: 'middle',
                            }}
                        >
                            <MTSLogo
                                style={{
                                    height: 'auto',
                                    width: isMobile ? '24px' : '32px',
                                }}
                            />
                        </Box>
                    </Typography>
                </Stack>
                <Stack
                    direction={isDesktop ? 'row' : 'column'}
                    spacing={isMobile ? 8 : 12}
                    width='100%'
                >
                    <Email
                        email={OPERATOR_EMAIL}
                        title={t('support')}
                    />
                    <Email
                        email={PR_EMAIL}
                        title={t('smi')}
                    >
                        <Typography
                            fontFamily='Hauora'
                            fontSize={useMediaSwitch('20px', '18px', '16px')}
                            fontWeight={500}
                            lineHeight={isDesktop ? '32px' : '24px'}
                            color='#969592'
                        >
                            {t('top')}
                            <NavHashLink to='#news' smooth exact>
                                {t('news')}
                            </NavHashLink>
                        </Typography>
                    </Email>
                </Stack>
                <Stack spacing={isMobile ? 3 : 4}>
                    <Title>
                        {t('address')}
                    </Title>
                    <Typography
                        fontFamily='Hauora'
                        fontSize={isMobile ? '20px' : '24px'}
                        fontWeight={700}
                        lineHeight='32px'
                        color='#242424'
                    >
                        119435 Москва, ул. Большой Саввинский пер., 8, стр. 1
                    </Typography>
                </Stack>
                <Typography
                    fontFamily='Hauora'
                    fontSize='16px'
                    fontWeight={500}
                    lineHeight='24px'
                    color='#969592'
                >
                    {t('copyright')}
                </Typography>
            </Stack>
        </Stack>
    );
};
